import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import TableComponent from "../../../../assets/common/table/TableComponent";
import Pagination from "../../../../assets/common/table/Pagination";
import AddJobGradeModal from "./AddJobGradeModal";

import { useJobGradeData } from "./customAddonsHooks/useJobGradeData";
import { useJobGradeColumns } from "./customAddonsHooks/useJobGradeColumns";
import { useCommonModals } from "./customAddonsHooks/useCommonModals";
import { useBulkOperations } from "./customAddonsHooks/useBulkOperations";

import PermissionContextFunctions from "../../../../assets/functions/PermissionContextFunctions";

import DeleteConfirmationModal from "../table/DeleteModal";
import Tick from "../../../../assets/customIcons/Tick";
import ExclaimRed from "../../../../assets/customIcons/ExclaimRed";

import stylesMain from "../../../../style/cssModules/company/Company.module.css";
import StatusMessages from "../../../../assets/common/table/StatusMessages";
import { notifySuccess, notifyWarn } from "../../../../assets/elements/Toast";
import MainServices from "../../../../services/MainServices";
import { deBounce } from "../../../../utils/deBounce";
import useConfig from "../../../../hooks/useConfig";

const moduleName = "Job Grade";

export default function JobGradeManagement({
  tabsOptions = [],
  handleTabChange = () => {},
  activeTab = "",
  // URL sync props from parent
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  addedFilters,
  setAddedFilters,
  addFilter,
  removeFilter,
}) {
  const config = useConfig();
  const { bulkStatusUpdate, getExportJobGradeData } = MainServices();

  /* ───────────────────── localisation */
  const { t, i18n } = useTranslation();

  /* ───────────────────── permissions */
  const { grantPermission } = PermissionContextFunctions();

  /* ───────────────────── refs */
  const dropdownRef = useRef(null);
  const threeDotsRef = useRef(null);

  /* ───────────────────── state */
  const [searchTerm, setSearchTerm] = useState("");
  const [editData, setEditData] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  /* ───────────────────── build API filters from URL sync filters */
  const buildApiFilters = useMemo(() => {
    return () => {
      const apiFilters = [];
      const queryParams = {};

      Object.values(addedFilters).forEach((filter) => {
        if (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== "" &&
          !(Array.isArray(filter.value) && filter.value.length === 0)
        ) {
          if (filter.isQueryParam) {
            queryParams[filter.apiKey] = filter.value;
          } else {
            const operator = filter.operator || "eq";
            apiFilters.push({
              key: filter.apiKey,
              [operator]: filter.value,
            });
          }
        }
      });

      return {
        filters: apiFilters,
        queryParams,
      };
    };
  }, [addedFilters]);

  const filtersKey = JSON.stringify(addedFilters);

  const {
    modals,
    openDeleteModal,
    closeDeleteModal,
    openSuccessModal,
    closeSuccessModal,
  } = useCommonModals();

  const {
    data,
    loading,
    totalCount,
    totalPages,
    noData,
    allSelected,
    setAllSelected,
    fetchData,
    toggleSelectAll,
    toggleRowSelection,
    updateStatus,
    deleteRecord,
  } = useJobGradeData({
    currentPage,
    itemsPerPage,
    buildApiFilters,
    openSuccessModal,
    closeDeleteModal,
  });

  /* ───────────────────── bulk helpers */
  const { bulkOperations, handleBulkStatusUpdate } = useBulkOperations({
    data,
    updateBulkStatus: (status, publicIds, selectedCount) => {
      // Step 1 ‣ Get all modal messages from StatusMessages
      const {
        title,
        titleData,
        message,
        messageData,
        button: confirmBtn,
        confirmTitle,
        confirmTitleData,
        confirmMessage,
        confirmMessageData,
      } = StatusMessages({
        moduleName,
        status,
        count: selectedCount,
        i18n,
      });

      // Step 2 ‣ Ask user confirmation before updating
      openDeleteModal({
        type: "bulk_status_change",
        data: publicIds,
        title,
        titleData,
        message,
        messageData,
        confirmText: confirmBtn,
        onConfirm: async () => {
          try {
            // Step 3 ‣ Perform update
            const details = {
              publicIds,
              moduleName: "JobGrade",
              status,
            };
            const response = await bulkStatusUpdate(details);

            // Step 4 ‣ Refresh and show success modal
            if (response?.data) {
              fetchData();
              closeDeleteModal();
              openSuccessModal({
                title: confirmTitle,
                confirmTitleData,
                message: confirmMessage,
                confirmMsgData: confirmMessageData,
                confirmText: `Go To Job Grade`,
              });
            }
          } catch (error) {
            notifyWarn(
              error?.response?.data?.message || "An unexpected error occurred"
            );
            console.error("bulkStatusUpdate error →", error);
          }
        },
      });
    },
  });

  /* ───────────────────── table columns */
  const { columns } = useJobGradeColumns({
    data,
    allSelected,
    toggleSelectAll,
    toggleRowSelection,
    handleEdit,
    handleDeactivate,
    handleDelete,
    handleBulkStatusUpdate,
    filters: addedFilters,
    addFilter,
    removeFilter,
  });

  /* ───────────────────── side-effects */
  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, filtersKey]);

  /* ───────────────────── handlers */
  // ⬅️ 1. make a single debounced callback, created once
  const debouncedFilter = useRef(
    deBounce((val) => {
      if (val) {
        addFilter("name", val, "name", false, "iLike");
      } else {
        removeFilter("name");
      }
    }, 500)
  ).current;

  const handleSearchTermDebounce = (value) => {
    setSearchTerm(value); // update the local text box
    debouncedFilter(value);
  };

  const handleExportClick = async () => {
    const selectedRows = data.filter((row) => row.isSelected);
    const publicIds = selectedRows.map((row) => row?.publicId);

    const { filters: apiFilters, queryParams } = buildApiFilters();

    const details = {
      filters: [
        ...apiFilters,
        ...(publicIds?.length
          ? [
              {
                key: "publicId",
                in: publicIds,
              },
            ]
          : []),
      ],
      ...queryParams,
    };

    try {
      setExportLoading(true);
      const response = await getExportJobGradeData(details);

      if (response?.data) {
        const exportFileName = response?.data?.data?.filePath;
        const fileLink = `${config?.exportLink}${exportFileName}`;

        // Create a hidden anchor element
        const link = document.createElement("a");
        link.href = fileLink;
        link.download = exportFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notifySuccess(t("Data Exported Successfully"));
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || t("An unexpected error occurred")
      );
      console.error("Error exporting job grade data", error);
    } finally {
      setExportLoading(false);
    }
  };

  function handleEdit(row) {
    setEditData(row);
    setAddModalOpen(true);
  }

  function handleDeactivate(row) {
    const nextStatus = row.status === "active" ? "inactive" : "active";

    const {
      title,
      titleData,
      message,
      messageData,
      button: confirmBtn,
    } = StatusMessages({
      moduleName: "Job Grade",
      status: nextStatus,
      count: 1,
      i18n,
    });

    openDeleteModal({
      type: "status_change",
      data: row,
      title,
      titleData,
      message,
      messageData,
      confirmText: confirmBtn,
      onConfirm: () => updateStatus(row),
    });
  }

  function handleDelete(row) {
    const {
      title,
      titleData,
      message,
      messageData,
      button: confirmBtn,
    } = StatusMessages({
      moduleName: "Job Grade",
      status: "delete",
      count: 1,
      i18n,
    });

    openDeleteModal({
      type: "delete",
      data: row,
      title,
      titleData,
      message,
      messageData,
      confirmText: confirmBtn || "Yes Delete",
      onConfirm: () => deleteRecord(row),
    });
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setEditData(null);
  };

  const handleAddModalSubmit = () => {
    setAddModalOpen(false);
    setEditData(null);

    openSuccessModal({
      title: editData
        ? "Job Grade Updated Successfully"
        : "Job Grade Added Successfully",
      confirmTitleData: {},
      message: "",
      confirmMsgData: {},
      confirmText: `Go To Job Grade`,
    });

    fetchData();
  };

  const handleItemsPerPageChange = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  /* ───────────────────── render */
  return (
    <>
      <div className="page-wrapper">
        <div className={stylesMain.footerHelper}>
          <div className="content">
            <div className="content-container">
              <TableComponent
                loading={loading}
                searchTerm={searchTerm}
                handleSearchTermDebounce={handleSearchTermDebounce}
                exportLoading={exportLoading}
                handleExportClick={handleExportClick}
                headerDescription={t(
                  "jobGrade.tableDescription",
                  "Manage job grades efficiently by adding, updating, or removing them. Ensure clear and structured job grade classifications within the organization."
                )}
                handleCloseMenu={() => {}}
                dropdownRef={dropdownRef}
                threeDotsRef={threeDotsRef}
                totalCount={totalCount}
                headerTitle={t("Job Grade")}
                headerPrimaryBtnName={
                  grantPermission("JobGrade:write") ? "Add Job Grade" : null
                }
                headerPrimaryBtnClick={() => setAddModalOpen(true)}
                columns={columns}
                data={data}
                noData={noData}
                isRead={grantPermission("JobGrade:read")}
                isTable={grantPermission("JobGrade:read")}
                isTabs
                tabsOptions={tabsOptions}
                handleTabChange={handleTabChange}
                activeTab={activeTab}
              />
            </div>
          </div>

          {!noData && !loading && (
            <Pagination
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* ───────────── add / edit modal */}
      {addModalOpen && (
        <AddJobGradeModal
          isOpen={addModalOpen}
          onClose={handleAddModalClose}
          handleOnSubmit={handleAddModalSubmit}
          handleOnUpdate={handleAddModalSubmit}
          getTableData={fetchData}
          editData={editData}
        />
      )}

      {/* ───────────── danger / delete modal */}
      {modals.delete.isOpen && (
        <DeleteConfirmationModal
          icon={<ExclaimRed />}
          isOpen={modals.delete.isOpen}
          headline={modals.delete.title}
          headlineData={modals.delete.titleData}
          paraText={modals.delete.message}
          paraTextData={modals.delete.messageData}
          buttonText="Cancel"
          confirmText={modals.delete.confirmText}
          isDelete
          onConfirm={modals.delete.onConfirm}
          onCancel={closeDeleteModal}
          onClose={closeDeleteModal}
        />
      )}

      {/* ───────────── success modal */}
      {modals.success.isOpen && (
        <DeleteConfirmationModal
          icon={<Tick />}
          isOpen={modals.success.isOpen}
          headline={modals.success.title}
          headlineData={modals.success.confirmTitleData}
          paraText={modals.success.message}
          paraTextData={modals.success.confirmMsgData}
          confirmText={modals.success.confirmText || "Go To Job Grade"}
          isConfirm
          onConfirm={closeSuccessModal}
          onCancel={closeSuccessModal}
          onClose={closeSuccessModal}
        />
      )}
    </>
  );
}
